/**
 * Wo die **Listen-Werkzeug-Pille** steht — links, mittig oder rechts unten in
 * der Aufgabenliste (CONTEXT.md, Listen-Werkzeug-Pille).
 *
 * **Gerätelokal und für alle Projekte**, wie die Seite der Pinnwand-Pille
 * (`canvasToolbarSide.ts`): Wer sie an den Rand schiebt, will sie dort
 * überall. Kein Sync — es ist Bequemlichkeit, kein Inhalt.
 *
 * Modul-Store mit Zuhörern, damit jede offene Liste sofort folgt; ein
 * `storage`-Ereignis feuert im selben Dokument nicht.
 *
 * Bewusst ohne React — die React-Hälfte steht in `hooks/useListToolbarPosition.ts`.
 */

export type ListToolbarPosition = 'left' | 'center' | 'right'

export const LIST_TOOLBAR_POSITION_KEY = 'happytask:list:toolbarPosition'

const DEFAULT_POSITION: ListToolbarPosition = 'center'

const isPosition = (value: unknown): value is ListToolbarPosition =>
  value === 'left' || value === 'center' || value === 'right'

/** `undefined` = noch nicht aus dem Speicher geholt. */
let position: ListToolbarPosition | undefined
const listeners = new Set<() => void>()

function readStored(): ListToolbarPosition {
  try {
    const raw = localStorage.getItem(LIST_TOOLBAR_POSITION_KEY)
    return isPosition(raw) ? raw : DEFAULT_POSITION
  } catch {
    // Im privaten Fenster wirft schon das Lesen.
    return DEFAULT_POSITION
  }
}

export function getListToolbarPosition(): ListToolbarPosition {
  if (position === undefined) position = readStored()
  return position
}

/** Nur bei einer echten Änderung melden — sonst renderte jede Liste umsonst. */
export function setListToolbarPosition(next: ListToolbarPosition): void {
  if (getListToolbarPosition() === next) return
  position = next
  try {
    localStorage.setItem(LIST_TOOLBAR_POSITION_KEY, next)
  } catch {
    // Kein Speicher (privates Fenster, volles Kontingent) — im laufenden Programm gilt sie trotzdem.
  }
  for (const listener of listeners) listener()
}

export function subscribeListToolbarPosition(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** Vergisst Position und Zuhörer — nur für Tests, die den Modulzustand teilen. */
export function resetListToolbarPositionForTests(): void {
  position = undefined
  listeners.clear()
}
